import {
  getGroupNames,
  FunnelMapKeys,
  groupStatesByFunnelGroup,
} from "@/util/funnelGroups";
import {
  FunnelStateDataPoint,
  FunnelChartDataQuery,
} from "@/__generated__/types";
import { FunnelBarData } from "@/components/Funnel/types";
import { SelectedFunnelBar } from "@/components/FunnelsChart";

export const processFunnelData = (
  data: FunnelStateDataPoint[],
  skipMelted = false,
): FunnelBarData[] => {
  let groupNames = getGroupNames("FUNNEL");
  if (skipMelted) {
    groupNames = groupNames.filter((name) => name !== "melted");
  }
  const groupedData = groupStatesByFunnelGroup(data, "FUNNEL");
  let previousCount = groupedData[groupNames[0]] || 0;

  return groupNames.map((group) => {
    const count = groupedData[group] || 0;
    const percent =
      previousCount === 0
        ? count > 0
          ? 100
          : undefined
        : Math.round((count / previousCount) * 100);
    previousCount = count;
    return {
      stage: group,
      count: groupedData[group] || 0,
      percent,
    };
  });
};

interface FunnelChartData {
  inCommunity: FunnelBarData[];
  notInCommunity: FunnelBarData[];
}

export const computeFunnelTableData = (data: FunnelChartData) => {
  function getRatio(
    dividend: FunnelMapKeys<"FUNNEL">,
    divisor: FunnelMapKeys<"FUNNEL">,
    data: FunnelBarData[],
  ) {
    const divisorValue =
      data.find((item) => item.stage === divisor)?.count || 0;
    const dividendValue =
      data.find((item) => item.stage === dividend)?.count || 0;

    if (divisorValue === 0) return `-`;

    return Math.round((dividendValue / divisorValue) * 100) + "%";
  }

  return [
    {
      label: "Inquiry to App Rate",
      inCommunity: getRatio("applied", "inquired", data.inCommunity),
      notInCommunity: getRatio("applied", "inquired", data.notInCommunity),
    },
    {
      label: "Applied To Commit",
      inCommunity: getRatio("committed", "applied", data.inCommunity),
      notInCommunity: getRatio("committed", "applied", data.notInCommunity),
    },
    {
      label: "Yield Rate",
      inCommunity: getRatio("enrolled", "admitted", data.inCommunity),
      notInCommunity: getRatio("enrolled", "admitted", data.notInCommunity),
    },
    {
      label: "Melt Rate",
      inCommunity: getRatio("melted", "committed", data.inCommunity),
      notInCommunity: getRatio("melted", "committed", data.notInCommunity),
    },
  ];
};

export const selectedFunnelBarsEqual = (
  bar1: SelectedFunnelBar,
  bar2: SelectedFunnelBar | null,
) =>
  bar1.stage === bar2?.stage &&
  bar1.communityStatus === bar2?.communityStatus &&
  bar1.funnel === bar2.funnel;

export const processQueryResponse = (
  data: FunnelChartDataQuery | undefined,
) => {
  let chartData: {
    inCommunity: FunnelBarData[];
    notInCommunity: FunnelBarData[];
  } = { inCommunity: [], notInCommunity: [] };

  if (
    data?.node?.__typename === "Organization" &&
    data?.node?.funnelChartData
  ) {
    const rawData = data?.node?.funnelChartData;
    chartData = {
      inCommunity: processFunnelData(rawData.inCommunity),
      notInCommunity: processFunnelData(rawData.notInCommunity),
    };
  }
  return chartData;
};
